import axios from 'axios'
import jwt_decode from 'jwt-decode'
import setAuthToken from './setAuthToken'

// Refresh slightly before the real expiry so a handshake never races the clock, and so small
// browser/server clock skew does not turn into a rejected connection.
const EXPIRY_SKEW_SECONDS = 30

// A handshake can also be refused while the local token still looks valid (clock skew, a token
// minted by an instance with a different secret). Force a refresh after this many consecutive
// failures so that case cannot spin forever either.
const FORCE_REFRESH_AFTER_FAILURES = 3

// The server was unreachable rather than refusing us — come back later instead of leaving the
// socket dead until the user reloads the page.
const UNREACHABLE_RETRY_MS = 5000

export const isTokenUsable = (token) => {
  if (!token) return false
  try {
    const { exp } = jwt_decode(token)
    if (!exp) return true
    return exp - EXPIRY_SKEW_SECONDS > Date.now() / 1000
  } catch (error) {
    return false
  }
}

// One in-flight refresh shared by every caller: several sockets going stale at the same moment
// must produce a single /auth/refresh-token call, not one per socket.
let refreshPromise = null

export const refreshAccessTokenOnce = () => {
  if (refreshPromise) return refreshPromise

  refreshPromise = axios
    .post('/auth/refresh-token')
    .then((response) => {
      const token = response?.data?.token
      if (!token) throw new Error('No token in refresh response')
      sessionStorage.setItem('CRMToken', token)
      setAuthToken(token)
      return token
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

/**
 * Keeps a socket's handshake token fresh and — the part that matters for the production log —
 * stops the reconnect loop when there is no usable token instead of retrying forever with the
 * expired one.
 *
 * Every refused handshake writes a `Socket.IO connection rejected: Token expired` line on the
 * server, and an idle tab has no HTTP traffic to trigger the axios 401 refresh, so a socket left
 * reconnecting on an expired token never heals itself: it just logs, once per attempt, forever.
 *
 * @param {object} socket socket.io-client socket
 * @returns {Function} teardown
 */
export const attachSocketTokenRefresh = (socket) => {
  let disposed = false
  let refreshing = false
  let consecutiveFailures = 0
  let retryTimer = null

  const applyToken = (token) => {
    socket.io.opts.query = `token=${encodeURIComponent(token)}`
  }

  const refreshThenReconnect = () => {
    if (refreshing || disposed) return
    refreshing = true
    consecutiveFailures = 0

    refreshAccessTokenOnce()
      .then((token) => {
        if (disposed) return
        applyToken(token)
        socket.connect()
      })
      .catch((error) => {
        if (disposed) return
        const status = error?.response?.status
        // 401/403 means the session itself is gone (logged out, ended elsewhere, or a tab that
        // never had one). Stay closed — the axios 401 interceptor owns the logout/redirect —
        // rather than hammering the server with a token it has already refused.
        if (status === 401 || status === 403) return
        // Anything else (network error, 5xx) is the backend being unreachable, not us being
        // unauthenticated, so keep trying.
        retryTimer = setTimeout(() => {
          if (!disposed) socket.connect()
        }, UNREACHABLE_RETRY_MS)
      })
      .finally(() => {
        refreshing = false
      })
  }

  const onReconnectAttempt = () => {
    const token = sessionStorage.getItem('CRMToken')

    if (isTokenUsable(token) && consecutiveFailures < FORCE_REFRESH_AFTER_FAILURES) {
      applyToken(token)
      return
    }

    // Abort this attempt — the manager re-checks skipReconnect right after this handler returns,
    // so disconnecting here cancels the pending open — and come back only once we hold a token
    // that can actually pass the handshake.
    socket.disconnect()
    refreshThenReconnect()
  }

  const onReconnectError = () => {
    consecutiveFailures += 1
  }

  const onConnect = () => {
    consecutiveFailures = 0
  }

  socket.io.on('reconnect_attempt', onReconnectAttempt)
  socket.io.on('reconnect_error', onReconnectError)
  socket.on('connect', onConnect)

  return () => {
    disposed = true
    if (retryTimer) clearTimeout(retryTimer)
    socket.io.off('reconnect_attempt', onReconnectAttempt)
    socket.io.off('reconnect_error', onReconnectError)
    socket.off('connect', onConnect)
  }
}
